/**
 * Pagination component — shared types and Zod prop schema.
 *
 * A horizontal dot indicator for multi-page flows (carousels,
 * onboarding, tutorials). Controlled: parent owns page state.
 *
 * Source of truth: Pangea Galapagos UI Design System — Beta
 * https://www.figma.com/design/Orc6uUodQaoXep59CGaUNw
 */

import { z } from 'zod';
import { colors } from '../../elements/colors';
import { spacing } from '../../elements/spacing';
import { radius } from '../../elements/radius';
import { duration } from '../../elements/animations';

/** Base shape — plain ZodObject so `formatZodType` can introspect props. */
const paginationBaseSchema = z.object({
  totalPages: z.number().int().min(1),
  currentPage: z.number().int().min(1),
});

/** Catalog-facing schema (plain ZodObject). */
export const paginationPropsSchema = paginationBaseSchema;

/** Runtime schema with cross-field refinements. */
export const paginationPropsRefinedSchema = paginationBaseSchema.refine(
  (data) => data.currentPage <= data.totalPages,
  {
    message: 'currentPage must be less than or equal to totalPages',
    path: ['currentPage'],
  },
);

export type PaginationProps = z.infer<typeof paginationPropsSchema>;

/** Dot height in pixels — also the width of an inactive (circular) dot. */
export const DOT_SIZE = spacing.s;

/** Width of the active dot — elongated into a pill; height stays DOT_SIZE. */
export const DOT_WIDTH_ACTIVE = spacing.m;

/** Gap between dots. */
export const DOT_GAP = spacing.s;

/** Border radius for dots — fully rounded. */
export const DOT_RADIUS = radius.rounded;

/** Colour of an inactive dot. */
export const DOT_COLOR_INACTIVE = colors.background.tertiary;

/** Colour of the active dot. */
export const DOT_COLOR_ACTIVE = colors.button.primary;

/** Animation duration for dot transitions (ms). */
export const DOT_TRANSITION_DURATION = duration.fade;

/** Container background colour (gray oval). */
export const CONTAINER_BACKGROUND = colors.background.secondary;

/** Container border radius — pill-shaped. */
export const CONTAINER_RADIUS = radius.rounded;

/** Container vertical padding. */
export const CONTAINER_PADDING_VERTICAL = spacing.s;

/** Container horizontal padding. */
export const CONTAINER_PADDING_HORIZONTAL = spacing.s;
